import { useRef, useState } from 'react';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import { AsyncDataHandler } from '@ds/elements/AsyncDataHandler/AsyncDataHandler';
import DocumentsEmpty from '@icons/documents-empty.svg';
import Download from '@icons/download.svg';
import styles from './ImmigrationDocuments.module.scss';
import { createDownloadHandler, formatPassportInfo } from '../ImmigrationStatus.service';
import { useGetImmigrationDocDetailsById } from 'src/shared/queries/useGetImmigrationDocDetailsById';
import { useGetCountries } from 'src/shared/queries/useGetCountries';
import { displayYesNo } from 'src/utils/services/common';
import { getFormattedDate, UserDateFormat } from 'src/utils/formatters/formatDate';
import { FallbackPlaceholderText } from 'src/utils/constants/common';

type LabeledValueProps = {
  label: string;
  value: string | null | undefined;
};

const LabeledValue = ({ label, value }: LabeledValueProps) => (
  <div className="flex items-center w-full md:max-w-[280px] p-4">
    <div>
      <label>{label}</label>
      <p>{value && value.trim() !== '' ? value : FallbackPlaceholderText.TripleDash}</p>
    </div>
  </div>
);

type ImmigrationDocDetailProps = {
  docId: number;
  beneId: number | undefined;
};

const ImmigrationDocDetail = ({ docId, beneId }: ImmigrationDocDetailProps) => {
  const { data: countries } = useGetCountries();
  const { data, isLoading, error } = useGetImmigrationDocDetailsById(docId);
  const [isProcessing, setIsProcessing] = useState(false);
  const toastRef = useRef<IToastr>(null);

  return (
    <AsyncDataHandler isLoading={isLoading} error={error} loader={{ width: '280px', height: '40px' }}>
      {!data ? (
        <p>No details available.</p>
      ) : (
        <>
          <div className={`flex flex-col items-center w-full`}>
            {data.documents?.map((doc) => (
              <div
                key={`document-${doc?.docId}`}
                className={`w-full ${styles['attachment']}`}
                onClick={() => {
                  if (isProcessing) return;
                  createDownloadHandler({ doc, beneId, toastRef, setIsProcessing })();
                }}
              >
                <span className={`${styles['name']}`}>
                  <img src={DocumentsEmpty} className={`${styles['document-icon']}`} />
                  <span>{doc.docName}</span>
                </span>
                <img src={Download} className={`${styles['download-icon']}`} />
              </div>
            ))}
          </div>

          <LabeledValue
            label="Issued Date"
            value={data.issuedDate == null ? null : getFormattedDate(data.issuedDate as string, UserDateFormat.get())}
          />

          <LabeledValue label="Issuing Agency" value={data.issuingAgency} />

          <LabeledValue label="Tracking?" value={displayYesNo(data.isTracking)} />

          <LabeledValue
            label="Active/Begining Date"
            value={data.beginDate == null ? null : getFormattedDate(data.beginDate as string, UserDateFormat.get())}
          />

          <LabeledValue label="Country/Location Where Issued" value={data.issuingCountryName} />

          <LabeledValue
            label="Reminder Date"
            value={data.reminderDate ? getFormattedDate(data.reminderDate, UserDateFormat.get()) : null}
          />

          <LabeledValue
            label="End Date"
            value={data.endDate ? getFormattedDate(data.endDate, UserDateFormat.get()) : null}
          />

          <LabeledValue label="Issuing City" value={data.issuingCity} />

          <LabeledValue label="Government Case Number" value={data.governmentCaseNumber} />

          <LabeledValue
            label="Expiration Date"
            value={data.expirationDate ? getFormattedDate(data.expirationDate, UserDateFormat.get()) : null}
          />

          <LabeledValue label="Authorized Work City" value={data.authorizedWorkCity} />

          <LabeledValue label="Passport Used" value={formatPassportInfo(data.usedPassport, countries)} />

          <LabeledValue
            label="Maximum Date"
            value={data.maximumDate ? getFormattedDate(data.maximumDate, UserDateFormat.get()) : null}
          />

          <LabeledValue label="Number of Entries" value={data.numberOfEntries} />

          <div className={`flex items-center w-full p-4 ${styles['notes']}`}>
            <div>
              <label>Notes</label>
              <p>{data.notes || FallbackPlaceholderText.TripleDash}</p>
            </div>
          </div>
        </>
      )}
    </AsyncDataHandler>
  );
};

export default ImmigrationDocDetail;
